/**
 * Application logger.
 *
 * In development: writes to the browser console so errors are visible while developing.
 * In production: forwards to Sentry (errors as exceptions, warnings as messages).
 * Both are no-ops if Sentry is not initialized, so callers never need to guard.
 */

import * as Sentry from '@sentry/react';

const isDev = import.meta.env.DEV;

export const logger = {
	error: (message: string, ...args: unknown[]): void => {
		if (isDev) {
			console.error(`[ERROR] ${message}`, ...args);
			return;
		}

		// Prefer a real Error so Sentry keeps its stack; otherwise synthesize one.
		const cause = args.find((arg): arg is Error => arg instanceof Error);
		Sentry.captureException(cause ?? new Error(message), {
			extra: { message, args },
		});
	},

	warn: (message: string, ...args: unknown[]): void => {
		if (isDev) {
			console.warn(`[WARN] ${message}`, ...args);
			return;
		}

		Sentry.captureMessage(message, { level: 'warning', extra: { args } });
	},

	// For expected, non-actionable events (e.g. form validation misses) that should
	// only add context to a later real error, not raise their own Sentry issue.
	breadcrumb: (message: string, data?: Record<string, unknown>): void => {
		if (isDev) {
			console.debug(`[BREADCRUMB] ${message}`, data);
			return;
		}

		Sentry.addBreadcrumb({ category: 'app', message, level: 'info', data });
	},
};
